// Reine Helfer des Akkord-Finders (Tab „Akkorde"). Die Griffe selbst sucht das Backend
// (`POST /api/chord-grips`, `griffschrift/selection/chord_finder.py`); hier stehen nur Auswahllisten,
// die Stimmungs-Rechnung und die Tonhöhen fürs Anhören.

import type {LocationQuery} from 'vue-router'

import {buttonMidis, INSTRUMENT_TUNINGS, PRECALC_DATA} from '@/components/harmonika/instrumentData'
import type {Grip} from '@/components/harmonika/gripTypes'

/** Grundton-Auswahl, Index = Tonklasse (deutsche Namen: B = b, H = h). */
export const ROOT_LABELS = [
  'C', 'C♯ / D♭', 'D', 'D♯ / E♭', 'E', 'F', 'F♯ / G♭', 'G', 'G♯ / A♭', 'A', 'B / A♯', 'H',
] as const

/** Kurzname je Tonklasse für Akkordnamen (`B` = b wie auf der Steirischen üblich). */
const ROOT_NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B', 'H'] as const

/** Die zwölf Akkordtypen; `id` = Kennung von `CHORD_TYPES` in `chord_finder.py`. */
export const CHORD_TYPE_OPTIONS = [
  {id: 'single', suffix: '', label: 'Einzelton'},
  {id: 'dur', suffix: '', label: 'Dur 3-Klang'},
  {id: 'moll', suffix: 'm', label: 'Moll 3-Klang'},
  {id: 'dim', suffix: 'dim', label: 'Verminderter 3-Klang'},
  {id: 'aug', suffix: 'aug', label: 'Übermäßiger 3-Klang'},
  {id: 'dur_7', suffix: '7', label: 'Dominant-Sept'},
  {id: 'dur_maj7', suffix: 'maj7', label: 'Major-Sept'},
  {id: 'moll_7', suffix: 'm7', label: 'Moll-Sept'},
  {id: 'dim_7', suffix: 'dim7', label: 'Vollvermindert Sept'},
  {id: 'dim_m7b5', suffix: 'm7b5', label: 'Halbvermindert Sept'},
  {id: 'sus4', suffix: 'sus4', label: 'Sus4 3-Klang'},
  {id: 'sus4_7', suffix: '7sus4', label: 'Sus4 Sept'},
] as const

export type ChordTypeId = (typeof CHORD_TYPE_OPTIONS)[number]['id']

export function rootName(pitchClass: number): string {
  return ROOT_NAMES[((pitchClass % 12) + 12) % 12]!
}

/** „C (Dur 3-Klang)", „F♯m7 (Moll-Sept)", „C Einzelton". */
export function chordOptionLabel(rootPc: number, type: (typeof CHORD_TYPE_OPTIONS)[number]): string {
  const root = rootName(rootPc)
  return type.id === 'single' ? `${root} ${type.label}` : `${root}${type.suffix} (${type.label})`
}

/**
 * Gehörter Grundton: was die eigene Harmonika spielt, wenn man den Griff für `griffRootPc`
 * (G-C-F-B-Raum) nimmt. Gleich dem gesuchten Grundton, solange „Stimmung" = eigene Harmonika
 * – beim Transponieren weicht er ab.
 */
export function heardRootPc(griffRootPc: number, instrumentOffset: number): number {
  return (((griffRootPc + instrumentOffset) % 12) + 12) % 12
}

/**
 * Knopf-Beschriftung in deutscher Schreibweise (`B` = b, `H` = h), passend zur Grundton-Auswahl
 * dieses Tabs. Nimmt die englischen Namen von `transposedNoteLabel` (`Bb4`, `B3`, `F#5`); eine
 * angehängte Oktave bleibt stehen.
 */
export function germanNoteLabel(label: string): string {
  const match = label.match(/^([A-G])(#|b)?(-?\d*)$/)
  if (!match) return label
  const [, letter, accidental = '', octave = ''] = match
  if (letter === 'B') return (accidental === 'b' ? 'B' : accidental === '#' ? 'H♯' : 'H') + octave
  if (letter === 'A' && accidental === '#') return `B${octave}`
  return letter + (accidental === '#' ? '♯' : accidental === 'b' ? '♭' : '') + octave
}

/** Die Töne eines Griffs (G-C-F-B-MIDI aus der Simulator-Tabelle, ohne enharmonische Dubletten). */
export function gripMidis(grip: Grip): number[] {
  const midis = new Set<number>()
  for (const button of grip.buttons) {
    const data = PRECALC_DATA[button.row - 1]?.buttons[button.number - 1]
    for (const midi of data ? buttonMidis(data, button.is_druck ? 'DRUCK' : 'ZUG') : []) midis.add(midi)
  }
  return [...midis].sort((a, b) => a - b)
}

/** Die Auswahl des Akkord-Finders – sie steht in der URL (`/akkorde?stimmung=…&grundton=…&akkord=…`). */
export interface ChordSelection {
  stimmungId: string
  rootPc: number
  chordType: ChordTypeId
}

const DEFAULT_ROOT_PC = 0
const DEFAULT_CHORD_TYPE: ChordTypeId = 'dur'

function firstValue(value: LocationQuery[string] | undefined): string | null {
  return (Array.isArray(value) ? value[0] : value) ?? null
}

/**
 * Liest die Auswahl aus der Query. Unbekanntes oder Fehlendes fällt auf den Default zurück –
 * ein von Hand geänderter oder veralteter Link zeigt so immer einen gültigen Akkord.
 * Ohne `stimmung` gilt die eigene Harmonika (`defaultTuningId`).
 */
export function parseChordQuery(query: LocationQuery, defaultTuningId: string): ChordSelection {
  const stimmung = firstValue(query.stimmung)
  const rootText = firstValue(query.grundton)
  const root = rootText !== null && /^\d{1,2}$/.test(rootText) ? Number(rootText) : NaN
  const akkord = firstValue(query.akkord)
  return {
    stimmungId: INSTRUMENT_TUNINGS.some((tuning) => tuning.id === stimmung) ? stimmung! : defaultTuningId,
    rootPc: root >= 0 && root <= 11 ? root : DEFAULT_ROOT_PC,
    chordType: CHORD_TYPE_OPTIONS.find((option) => option.id === akkord)?.id ?? DEFAULT_CHORD_TYPE,
  }
}

/** Die Query zu einer Auswahl – nur Abweichungen vom Default, damit der Standard-Link `/akkorde` bleibt. */
export function chordQueryPatch(selection: ChordSelection, defaultTuningId: string): Record<string, string> {
  const query: Record<string, string> = {}
  if (selection.stimmungId !== defaultTuningId) query.stimmung = selection.stimmungId
  if (selection.rootPc !== DEFAULT_ROOT_PC) query.grundton = String(selection.rootPc)
  if (selection.chordType !== DEFAULT_CHORD_TYPE) query.akkord = selection.chordType
  return query
}
